/**
 * Iter XX.291 — Second Innings canonical booking page.
 *
 * URL: `/second-innings/book?rid=<hex>&t=<hmac>`
 *
 * One permanent personalized link per registrant. Dynamically renders
 * every session currently in `pre_sale` with remaining capacity — the
 * organizer adds/updates sessions via the /admin/sessions endpoints,
 * NO code deploy required.
 *
 * Flow:
 *   1. Verify personalized link → prefill name/email + prior bookings
 *   2. Fetch active sessions
 *   3. Player selects ONE session card (radio-style)
 *   4. Accept mandatory waiver → single "Reserve my spot — $30" CTA
 *   5. Redirect to Stripe → return to `/second-innings/session-1/success`
 *
 * Design intent:
 *   * No hard-coded dates anywhere. Missing fields render as "TBD".
 *   * If the customer already paid a session, that card is disabled
 *     with a "Booked" badge. They can still book OTHER open sessions.
 *   * Preserves every XX.290-MIN protection (waiver gate, capacity,
 *     status, refund disclosure) — this file is purely a multi-slot
 *     selector on top of the same checkout endpoint.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";

const WAIVER_VERSION = "2026-02-v1";
const DISCLOSURE_FALLBACK =
  "We're finalizing the booking for this session. "
  + "If Second Innings cannot confirm it, you receive a full $30 refund.";

export default function SecondInningsBook() {
  const [params] = useSearchParams();
  const rid = params.get("rid") || "";
  const t   = params.get("t") || "";

  const [reg, setReg]                   = useState(null);
  const [sessions, setSessions]         = useState([]);
  const [priceCents, setPriceCents]     = useState(3000);
  const [disclosure, setDisclosure]     = useState(DISCLOSURE_FALLBACK);
  const [status, setStatus]             = useState("loading");
  const [error, setError]               = useState(null);
  const [submitting, setSub]            = useState(false);
  const [selectedKey, setSelectedKey]   = useState("");
  const [waiverAccepted, setWaiverAcc]  = useState(false);
  const [mediaConsent, setMediaConsent] = useState(false);
  const [waiverOpen, setWaiverOpen]     = useState(false);
  // Which session_keys the registrant has already paid — used to
  // disable those cards. Populated from prior /session-1/registrant
  // call PLUS any per-session booking lookups the backend surfaces.
  const [paidKeys, setPaidKeys] = useState(new Set());

  useEffect(() => {
    let cancel = false;
    (async () => {
      if (!rid || !t) { setStatus("bad_link"); return; }
      try {
        const [regRes, listRes] = await Promise.all([
          api.get(
            `/second-innings/session-1/registrant?rid=${encodeURIComponent(rid)}&t=${encodeURIComponent(t)}`,
          ),
          api.get("/second-innings/sessions/active"),
        ]);
        if (cancel) return;
        setReg(regRes.data);
        // XX.291.1 — read the FULL list of session_keys this
        // registrant has already paid for. Pre-XX.291.1 backends
        // won't return `paid_session_keys`; fall back to the legacy
        // single-session field only when it is present AND paid.
        const paid = Array.isArray(regRes.data?.paid_session_keys)
          ? regRes.data.paid_session_keys.filter(Boolean)
          : [];
        const legacy = regRes.data?.existing_booking;
        if (paid.length === 0 && legacy?.status === "paid" && legacy?.booking_id) {
          paid.push("session_1");
        }
        setPaidKeys(new Set(paid));
        const list = listRes.data?.sessions || [];
        setSessions(list);
        setPriceCents(listRes.data?.price_cents || 3000);
        setDisclosure(listRes.data?.disclosure_text || DISCLOSURE_FALLBACK);
        // Auto-select the first eligible (non-paid, non-full) session
        // so the common flow is a two-click journey.
        const paidSet = new Set(paid);
        const firstEligible = list.find(
          (s) => !paidSet.has(s.session_key) && Number(s.spots_remaining || 0) > 0,
        );
        if (firstEligible) setSelectedKey(firstEligible.session_key);
        setStatus("ready");
      } catch (e) {
        setStatus(e?.response?.status === 401 ? "bad_link" : "not_found");
      }
    })();
    return () => { cancel = true; };
  }, [rid, t]);

  const priceLabel = useMemo(() => `$${(priceCents / 100).toFixed(0)}`, [priceCents]);

  const onReserve = async () => {
    setError(null);
    if (!selectedKey) {
      setError("Please choose a session first.");
      return;
    }
    if (!waiverAccepted) {
      setError("Please read and accept the Participant Waiver to continue.");
      return;
    }
    setSub(true);
    try {
      const { data } = await api.post(
        "/second-innings/session-1/bookings/checkout",
        {
          rid,
          t,
          session_key: selectedKey,
          waiver_accepted: true,
          waiver_version:  WAIVER_VERSION,
          media_consent:   mediaConsent,
        },
      );
      if (data?.url) {
        window.location.assign(data.url);
      } else {
        setError("Something went wrong starting checkout.");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail === "waiver_required") {
        setError("Please read and accept the Participant Waiver to continue.");
      } else if (detail === "session_not_open") {
        setError("Reservations for that session aren't open. Please pick another.");
      } else if (detail === "session_full") {
        setError("This session just filled up. Please choose another.");
      } else if (detail === "already_booked") {
        setPaidKeys((prev) => new Set([...prev, selectedKey]));
        setError("You've already reserved a spot for that session.");
      } else if (detail === "session_not_found") {
        setError("That session is no longer available. Please refresh.");
      } else {
        setError(detail || "Checkout failed. Please try again.");
      }
    } finally {
      setSub(false);
    }
  };

  if (status === "loading") {
    return <_Shell><p className="text-neutral-400">Loading…</p></_Shell>;
  }
  if (status === "bad_link") {
    return <_Shell>
      <_Err title="This link isn't valid">
        Please open the personalized link from your Second Innings email.
        If it's expired, reply to that email and we'll send a fresh one.
      </_Err>
    </_Shell>;
  }
  if (status === "not_found") {
    return <_Shell>
      <_Err title="We couldn't find your registration">
        This link points to a registrant that no longer exists. Please
        reply to the Second Innings email so we can look into it.
      </_Err>
    </_Shell>;
  }

  return (
    <_Shell>
      <header className="mb-6" data-testid="si-book-header">
        <div className="text-[13px] uppercase tracking-[0.14em] text-emerald-300/80">
          Second Innings
        </div>
        <h1 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-neutral-50">
          Pick a session, reserve your spot.
        </h1>
        <p className="mt-4 text-[15px] leading-relaxed text-neutral-300">
          <span className="text-emerald-300">{priceLabel}</span> per session ·
          {" "}Just show up. Play cricket. Have fun. Go home.
        </p>
      </header>

      <section className="rounded-2xl border border-neutral-800 bg-neutral-950 p-5 sm:p-6 space-y-5">
        <div>
          <div className="text-[12px] uppercase tracking-[0.14em] text-neutral-500">
            Hi
          </div>
          <div className="mt-1 text-lg text-neutral-100" data-testid="si-book-name">
            {reg?.first_name}
          </div>
          <div className="text-[13px] text-neutral-500" data-testid="si-book-email">
            {reg?.email}
          </div>
        </div>

        {/* XX.291 — dynamic session cards. One radio per session. */}
        <div>
          <div className="text-[14px] font-medium text-neutral-100">
            Available sessions
          </div>
          {sessions.length === 0 ? (
            <p
              className="mt-3 text-[13px] text-neutral-400"
              data-testid="si-book-no-sessions"
            >
              No sessions are open for booking right now. We'll email you
              as soon as the next one opens.
            </p>
          ) : (
            <ul
              className="mt-3 space-y-2"
              role="radiogroup"
              aria-label="Available sessions"
              data-testid="si-book-session-list"
            >
              {sessions.map((s) => {
                const alreadyPaid = paidKeys.has(s.session_key);
                const remaining   = Number(s.spots_remaining || 0);
                const disabled    = alreadyPaid || remaining <= 0;
                const isSelected  = selectedKey === s.session_key;
                return (
                  <li key={s.session_key}>
                    <label
                      className={
                        "flex items-start gap-3 rounded-xl border px-4 py-3 transition-colors "
                        + (disabled
                            ? "border-neutral-900 bg-neutral-950/60 cursor-not-allowed opacity-60"
                            : isSelected
                              ? "border-emerald-600 bg-emerald-950/20 cursor-pointer"
                              : "border-neutral-800 bg-neutral-900/60 hover:border-neutral-700 cursor-pointer")
                      }
                      data-testid={`si-book-session-card-${s.session_key}`}
                    >
                      <input
                        type="radio"
                        name="si-session"
                        className="mt-1 h-4 w-4 shrink-0 accent-emerald-600"
                        checked={isSelected}
                        disabled={disabled}
                        onChange={() => setSelectedKey(s.session_key)}
                        data-testid={`si-book-session-radio-${s.session_key}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-neutral-100 text-[15px]">
                          {s.proposed_date_label || "TBD"}
                        </div>
                        <div className="text-neutral-400 text-[13px] mt-0.5">
                          {s.proposed_time_label || "TBD"} · America/Toronto
                        </div>
                        <div className="text-neutral-300 text-[13px] mt-2">
                          <span className="text-neutral-500">Venue:</span>{" "}
                          {s.venue_name || "TBD"}
                          {!s.venue_confirmed && s.venue_name && (
                            <span className="text-neutral-400"> (proposed — being finalized)</span>
                          )}
                        </div>
                        <div className="text-neutral-500 text-[12px] mt-2" data-testid={`si-book-remaining-${s.session_key}`}>
                          {alreadyPaid
                            ? "Booked — you're already in for this one"
                            : `${remaining} of ${s.capacity} spots remaining`}
                        </div>
                      </div>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {sessions.length > 0 && (
          <>
            {/* Waiver + optional media consent. */}
            <div
              className="pt-3 space-y-3 border-t border-neutral-900"
              data-testid="si-book-consents"
            >
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={waiverAccepted}
                  onChange={(e) => setWaiverAcc(e.target.checked)}
                  data-testid="si-book-waiver-checkbox"
                  className="mt-1 h-4 w-4 shrink-0 accent-emerald-600"
                />
                <span className="text-[13px] leading-relaxed text-neutral-300">
                  I have read and agree to the{" "}
                  <button
                    type="button"
                    onClick={() => setWaiverOpen(true)}
                    data-testid="si-book-waiver-open"
                    className="text-emerald-300 underline underline-offset-2 hover:text-emerald-200"
                  >
                    Participant Waiver
                  </button>
                  {" "}and Assumption of Risk.
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={mediaConsent}
                  onChange={(e) => setMediaConsent(e.target.checked)}
                  data-testid="si-book-media-consent-checkbox"
                  className="mt-1 h-4 w-4 shrink-0 accent-emerald-600"
                />
                <span className="text-[13px] leading-relaxed text-neutral-400">
                  <span className="text-neutral-300">Optional:</span>{" "}
                  I'm okay with photos/video of me being used in Second Innings/Gully
                  social media and event promotion.
                </span>
              </label>
            </div>

            <p
              className="pt-2 text-[13px] leading-relaxed text-neutral-300"
              data-testid="si-book-disclosure"
            >
              {disclosure}
            </p>

            {error && (
              <div
                className="rounded-lg border border-red-900/40 bg-red-950/30 px-4 py-3 text-[14px] text-red-200"
                data-testid="si-book-error"
              >
                {error}
              </div>
            )}

            <button
              type="button"
              data-testid="si-book-reserve-cta"
              disabled={submitting || !waiverAccepted || !selectedKey}
              onClick={onReserve}
              className={
                "w-full inline-flex items-center justify-center gap-2 rounded-xl px-5 py-4 "
                + "text-[16px] font-medium transition-colors "
                + (submitting || !waiverAccepted || !selectedKey
                    ? "bg-neutral-800 text-neutral-500 cursor-not-allowed"
                    : "bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 text-white")
              }
            >
              {submitting
                ? "Opening checkout…"
                : `Reserve my spot — ${priceLabel}`}
            </button>
          </>
        )}
      </section>

      {waiverOpen && <_WaiverModal onClose={() => setWaiverOpen(false)} />}
    </_Shell>
  );
}

function _Shell({ children }) {
  return (
    <main className="min-h-screen bg-neutral-950 text-neutral-100">
      <div className="max-w-xl mx-auto px-4 sm:px-6 pt-10 pb-24">
        {children}
      </div>
    </main>
  );
}

function _Err({ title, children }) {
  return (
    <div className="rounded-2xl border border-red-900/40 bg-red-950/30 p-6">
      <div className="text-[13px] uppercase tracking-[0.14em] text-red-300/80">
        {title}
      </div>
      <p className="mt-3 text-[15px] leading-relaxed text-neutral-200">
        {children}
      </p>
    </div>
  );
}

// Same waiver text as SecondInningsSession1.jsx. Kept locally for
// zero cross-file coupling — the modal is a leaf component.
function _WaiverModal({ onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="si-book-waiver-title"
      onClick={onClose}
      data-testid="si-book-waiver-modal"
    >
      <div
        className="w-full sm:max-w-lg max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl border border-neutral-800 bg-neutral-950 pb-mobile-nav"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 sm:px-6 pt-6 pb-5 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <h2
              id="si-book-waiver-title"
              className="text-xl font-semibold tracking-tight text-neutral-50"
            >
              Second Innings Participant Waiver &amp; Assumption of Risk
            </h2>
            <button
              type="button"
              onClick={onClose}
              data-testid="si-book-waiver-close"
              className="text-neutral-400 hover:text-neutral-100 text-2xl leading-none"
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <ol className="space-y-3 text-[14px] leading-relaxed text-neutral-300 list-decimal list-outside pl-5">
            <li>
              I understand that cricket and related physical activities involve
              risks, including falls, collisions, being struck by a ball or
              equipment, muscle or joint injuries, and other injuries that may
              occur during participation.
            </li>
            <li>
              I voluntarily choose to participate in Second Innings activities
              and accept the ordinary risks associated with doing so.
            </li>
            <li>
              I confirm that I am physically able to participate and will stop
              participating if I feel unsafe or unwell.
            </li>
            <li>
              To the extent permitted by law, I release Second Innings, its
              organizers, hosts, venue partners, volunteers and representatives
              from claims arising from the ordinary risks of participating in
              the event, except where liability cannot legally be excluded.
            </li>
            <li>
              I agree to follow event and venue safety instructions and
              understand that unsafe behaviour may result in removal from the
              session.
            </li>
            <li>
              If I am registering on behalf of a participant under 18, I confirm
              that I am their parent or legal guardian and consent to their
              participation.
            </li>
          </ol>
          <p className="pt-2 border-t border-neutral-900 text-[13px] leading-relaxed text-neutral-400">
            By checking the box, I confirm that I have read, understood and
            agree to this waiver.
          </p>
          <button
            type="button"
            onClick={onClose}
            data-testid="si-book-waiver-ok"
            className="w-full rounded-xl bg-neutral-800 hover:bg-neutral-700 text-neutral-100 py-3 text-[15px] font-medium transition-colors"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
